import React, { useEffect, useState, createContext, useContext, createElement } from 'react';
import { translations, Language, supportedLanguages } from '../lib/translations';
interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: typeof translations.en;
}
const LanguageContext = createContext<LanguageContextType | undefined>(undefined);
function detectLanguageFromPath(): Language {
  const path = window.location.pathname.split('/')[1] || '';
  const found = supportedLanguages.find(l => l.code === path);
  return found ? found.code : 'en';
}
export function LanguageProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [language, setLanguageState] = useState<Language>(detectLanguageFromPath());
  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    const targetLang = supportedLanguages.find(l => l.code === lang);
    if (targetLang) {
      window.history.pushState({}, '', targetLang.path);
    }
  };
  useEffect(() => {
    // Update html lang attribute for SEO
    // Note: path uses 'ua' for Ukraine but BCP-47 lang code must be 'uk'
    const getLangCode = (lang: Language): string => {
      if (lang === 'pt-br') return 'pt-BR';
      if (lang === 'ua') return 'uk';
      return lang;
    };
    document.documentElement.lang = getLangCode(language);
    // Use hardcoded BASE_URL to ensure hreflang tags always point to
    // the production domain, not localhost or staging environments
    const BASE_URL = 'https://magicaibuilders.com';
    // Add/update hreflang meta tags
    const existingLinks = document.querySelectorAll('link[hreflang]');
    existingLinks.forEach(el => el.remove());
    supportedLanguages.forEach(lang => {
      const link = document.createElement('link');
      link.rel = 'alternate';
      link.hreflang = getLangCode(lang.code);
      link.href = `${BASE_URL}${lang.path}`;
      document.head.appendChild(link);
    });
    // x-default
    const defaultLink = document.createElement('link');
    defaultLink.rel = 'alternate';
    defaultLink.hreflang = 'x-default';
    defaultLink.href = `${BASE_URL}/`;
    document.head.appendChild(defaultLink);
  }, [language]);
  // Listen for popstate (browser back/forward)
  useEffect(() => {
    const handlePopState = () => {
      setLanguageState(detectLanguageFromPath());
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);
  const value = {
    language,
    setLanguage,
    t: translations[language]
  };
  return <LanguageContext.Provider value={value} data-id="element-541">
      {children}
    </LanguageContext.Provider>;
}
export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}